// ============================================================================
// OpenClaw Gateway WebSocket Client
//
// Handles: connection lifecycle, challenge-nonce auth, typed RPC calls,
// event subscriptions, and auto-reconnect with exponential backoff.
//
// Usage with Redux:
//   const client = new OpenClawGatewayClient({ url, token, onEvent, onStatusChange });
//   client.connect();
//   await client.rpc("chat.send", { text: "Hello", sessionKey: "my-session" });
//   client.disconnect();
// ============================================================================
// ---------------------------------------------------------------------------
// Auth Management
// ---------------------------------------------------------------------------
class SecureArray {
    contents;
    length;
    constructor(array) {
        this.contents = array;
        this.length = array.length;
    }
    getContents() {
        return this.contents;
    }
    [Symbol.dispose]() {
        this.contents.fill(0);
    }
}
class DataLock {
    static ALGORITHM = "AES-GCM";
    static DERIVATION_ALGO = "PBKDF2";
    static ITERATIONS = 100000; // High count for security
    static SALT_LENGTH = 32;
    static IV_LENGTH = 32;
    static SALT_AND_IV_LENGTH = this.SALT_LENGTH + this.IV_LENGTH;
    /** Encrypts a string using a password */
    async lock(plaintext, password) {
        const encoder = new TextEncoder();
        const salt = new SecureArray(window.crypto.getRandomValues(new Uint8Array(DataLock.SALT_LENGTH)));
        const iv = new SecureArray(window.crypto.getRandomValues(new Uint8Array(DataLock.IV_LENGTH)));
        const key = await this.deriveKey(password, salt);
        const ciphertext = await window.crypto.subtle.encrypt({ name: DataLock.ALGORITHM, iv: iv.getContents() }, key, encoder.encode(plaintext));
        // Combine Salt + IV + Ciphertext for storage (Base64 encoded)
        const combined = new Uint8Array(salt.length + iv.length + ciphertext.byteLength);
        combined.set(salt.getContents(), 0);
        combined.set(iv.getContents(), salt.length);
        combined.set(new Uint8Array(ciphertext), salt.length + iv.length);
        return btoa(String.fromCharCode(...combined));
    }
    async unlock(encryptedBase64, password) {
        const decoder = new TextDecoder();
        const combined = Uint8Array.from(atob(encryptedBase64), (c) => c.charCodeAt(0));
        // Extract pieces
        const salt = new SecureArray(combined.slice(0, DataLock.SALT_LENGTH));
        const iv = combined.slice(DataLock.SALT_LENGTH, DataLock.SALT_AND_IV_LENGTH);
        const ciphertext = combined.slice(DataLock.SALT_AND_IV_LENGTH);
        const key = await this.deriveKey(password, salt);
        try {
            const decrypted = await window.crypto.subtle.decrypt({ name: DataLock.ALGORITHM, iv }, key, ciphertext);
            return decoder.decode(decrypted);
        }
        catch {
            throw new Error("Invalid password or corrupted data.");
        }
    }
    async deriveKey(password, salt) {
        const baseKey = await window.crypto.subtle.importKey("raw", password.getContents(), "PBKDF2", false, ["deriveKey"]);
        return window.crypto.subtle.deriveKey({
            name: DataLock.DERIVATION_ALGO,
            salt: salt.getContents(),
            iterations: DataLock.ITERATIONS,
            hash: "SHA-256"
        }, baseKey, { name: DataLock.ALGORITHM, length: 256 }, false, ["encrypt", "decrypt"]);
    }
}
class SecureStorageManager {
    locker = new DataLock();
    STORAGE_KEY = "openclaw-key";
    constructor(keyPath) {
        this.STORAGE_KEY = keyPath;
    }
    async saveData(data, password) {
        try {
            const jsonString = JSON.stringify(data);
            const encrypted = await this.locker.lock(jsonString, password);
            // Save to localStorage
            localStorage.setItem(this.STORAGE_KEY, encrypted);
        }
        catch (e) {
            if (e instanceof DOMException && e.name === "QuotaExceededError") {
                throw new Error("Storage limit exceeded (max ~5MB).");
            }
            throw e;
        }
    }
    async loadData(password) {
        const encrypted = localStorage.getItem(this.STORAGE_KEY);
        if (!encrypted)
            return null;
        try {
            const decryptedJson = await this.locker.unlock(encrypted, password);
            return JSON.parse(decryptedJson);
        }
        catch (err) {
            // Typically happens if the password is wrong
            throw new Error("Decryption failed. Check your password.");
        }
    }
    clear() {
        localStorage.removeItem(this.STORAGE_KEY);
    }
}
class OpenClawClientSecurityAdapter {
    ALGO = "Ed25519";
    CAPABILITIES = ["sign", "verify"];
    storageManager = new SecureStorageManager("open-claw-key");
    storage_secret = undefined;
    constructor(storage_secret) {
        if (storage_secret) {
            const encoder = new TextEncoder();
            const buffer = encoder.encode(storage_secret);
            this.storage_secret = new SecureArray(buffer);
        }
    }
    /**
     * Helper to convert ArrayBuffer/Uint8Array to lowercase hex string
     */
    bytesToHex(bytes) {
        return Array.from(bytes)
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("");
    }
    bytesToBase64Url(bytes) {
        return btoa(String.fromCharCode(...bytes))
            .split("+")
            .join("-")
            .split("/")
            .join("_")
            .split("=")[0];
    }
    async openClawSignFlow(config) {
        const keyPair = await this.getKeyPair();
        const rawPublicKey = await crypto.subtle.exportKey("raw", keyPair.publicKey);
        const hashBuffer = await crypto.subtle.digest("SHA-256", rawPublicKey);
        const deviceId = this.bytesToHex(new Uint8Array(hashBuffer));
        if (deviceId.length !== 64)
            console.error("Device ID was not the right size");
        const signedAtMs = Date.now().toString();
        const payload = [
            "v2",
            deviceId,
            config.clientId,
            config.clientMode,
            config.role,
            config.scopes,
            signedAtMs,
            config.token,
            config.nonce ?? ""
        ].join("|");
        const encoder = new TextEncoder();
        const signatureBuffer = await crypto.subtle.sign({ name: "Ed25519" }, keyPair.privateKey, encoder.encode(payload));
        const publicKey = this.bytesToBase64Url(new Uint8Array(rawPublicKey));
        let deviceAuth = {
            id: deviceId,
            publicKey,
            signature: this.bytesToBase64Url(new Uint8Array(signatureBuffer)),
            signedAt: parseInt(signedAtMs)
        };
        if (config.nonce) {
            deviceAuth["nonce"] = config.nonce;
        }
        return deviceAuth;
    }
    async getKeyPair() {
        if (this.storage_secret) {
            const data = await this.storageManager.loadData(this.storage_secret).catch(() => null);
            if (data) {
                const keyPair = {
                    privateKey: await crypto.subtle.importKey("jwk", data.privateKey, this.ALGO, true, this.CAPABILITIES),
                    publicKey: await crypto.subtle.importKey("jwk", data.publicKey, this.ALGO, true, this.CAPABILITIES)
                };
                return keyPair;
            }
        }
        const newKeyPair = await crypto.subtle.generateKey(this.ALGO, true, // extractable
        this.CAPABILITIES);
        if (this.storage_secret) {
            const obj = {
                privateKey: await crypto.subtle.exportKey("jwk", newKeyPair.privateKey),
                publicKey: await crypto.subtle.exportKey("jwk", newKeyPair.publicKey)
            };
            this.storageManager.saveData(obj, this.storage_secret);
        }
        return newKeyPair;
    }
}
// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------
export class OpenClawGatewayClient {
    securityAdapter = new OpenClawClientSecurityAdapter();
    ws = null;
    config;
    status = "disconnected";
    pendingRpc = new Map();
    reconnectAttempt = 0;
    reconnectTimer = null;
    challenge = null;
    disposed = false;
    constructor(config) {
        this.config = {
            clientId: "aible-webchat",
            clientVersion: "1.0.0",
            autoReconnect: true,
            ...config
        };
    }
    // -------------------------------------------------------------------------
    // Public API
    // -------------------------------------------------------------------------
    /** Open the WebSocket connection to the Gateway */
    connect() {
        if (this.disposed)
            return;
        if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
            return;
        }
        this.setStatus("connecting");
        this.challenge = null;
        try {
            this.ws = new WebSocket(this.config.url);
            this.ws.onopen = this.handleOpen;
            this.ws.onmessage = this.handleMessage;
            this.ws.onclose = this.handleClose;
            this.ws.onerror = this.handleError;
        }
        catch (err) {
            console.error("[OpenClaw] WebSocket construction failed:", err);
            this.setStatus("error");
            this.scheduleReconnect();
        }
    }
    /** Gracefully close the connection */
    disconnect() {
        this.disposed = true;
        this.clearReconnectTimer();
        this.rejectAllPending("Client disconnected");
        if (this.ws) {
            this.ws.onclose = null; // prevent reconnect
            this.ws.close();
            this.ws = null;
        }
        this.setStatus("disconnected");
    }
    /** Send a typed RPC request and await the response */
    async rpc(method, params) {
        return new Promise((resolve, reject) => {
            if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
                return reject(new Error(`[OpenClaw] Cannot call ${method}: not connected`));
            }
            const id = crypto.randomUUID();
            const timer = setTimeout(() => {
                this.pendingRpc.delete(id);
                reject(new Error(`[OpenClaw] RPC timeout: ${method} (id=${id})`));
            }, 60_000); // 60s timeout (chat.send can be slow)
            this.pendingRpc.set(id, { resolve, reject, timer });
            const frame = JSON.stringify({ type: "req", id, method, params });
            this.ws.send(frame);
        });
    }
    /** Check if connected */
    get isConnected() {
        return this.status === "connected";
    }
    /** Get current status */
    get connectionStatus() {
        return this.status;
    }
    // -------------------------------------------------------------------------
    // WebSocket event handlers
    // -------------------------------------------------------------------------
    handleOpen = () => {
        this.reconnectAttempt = 0;
        this.setStatus("authenticating");
        // Wait for the connect.challenge event from the gateway
    };
    handleMessage = (evt) => {
        let frame;
        try {
            frame = JSON.parse(evt.data);
        }
        catch {
            console.warn("[OpenClaw] Failed to parse frame:", evt.data);
            return;
        }
        switch (frame.type) {
            case "event":
                this.handleEventFrame(frame);
                break;
            case "res":
                this.handleResponseFrame(frame);
                break;
            default:
                // Ignore unknown frame types
                break;
        }
    };
    handleClose = (evt) => {
        console.warn("[OpenClaw] WebSocket closed:", evt.code, evt.reason);
        this.ws = null;
        this.rejectAllPending("Connection closed");
        if (!this.disposed && this.config.autoReconnect) {
            this.setStatus("reconnecting");
            this.scheduleReconnect();
        }
        else {
            this.setStatus("disconnected");
        }
    };
    handleError = (_evt) => {
        console.error("[OpenClaw] WebSocket error");
        // onclose will fire after onerror, so let that handle reconnect
    };
    // -------------------------------------------------------------------------
    // Frame processing
    // -------------------------------------------------------------------------
    handleEventFrame(frame) {
        // Handle the auth challenge before forwarding other events
        if (frame.event === "connect.challenge") {
            const challenge = frame.payload;
            this.challenge = challenge ?? null;
            this.sendConnectRequest();
            return;
        }
        // Forward all events to the subscriber
        this.config.onEvent?.(frame);
    }
    handleResponseFrame(frame) {
        const pending = this.pendingRpc.get(frame.id);
        // Handle the connect response specially
        if (pending === undefined && frame.id) {
            // This might be a connect response we didn't track via rpc()
            // Just check if it's hello-ok
            if (frame.ok && frame.payload?.type === "hello-ok") {
                this.setStatus("connected");
                return;
            }
            if (!frame.ok) {
                console.error("[OpenClaw] Connect rejected:", frame.error);
                this.setStatus("error");
            }
            return;
        }
        if (!pending)
            return;
        clearTimeout(pending.timer);
        this.pendingRpc.delete(frame.id);
        if (frame.ok) {
            pending.resolve(frame.payload);
        }
        else {
            pending.reject(frame.error ?? new Error("RPC failed"));
        }
    }
    // -------------------------------------------------------------------------
    // Auth handshake
    // -------------------------------------------------------------------------
    connectRequestId = null;
    async sendConnectRequest() {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN)
            return;
        this.connectRequestId = crypto.randomUUID();
        let params = {
            minProtocol: 3,
            maxProtocol: 4,
            client: {
                id: "webchat",
                version: this.config.clientVersion,
                platform: typeof navigator !== "undefined" ? navigator.platform || "web" : "web",
                mode: "webchat"
            },
            role: "operator",
            scopes: ["operator.read", "operator.write", "operator.admin"],
            caps: ["tool-events"],
            commands: [],
            permissions: {},
            auth: { token: this.config.token },
            locale: typeof navigator !== "undefined" ? navigator.language || "en-US" : "en-US",
            userAgent: `aible-openclaw-client/${this.config.clientVersion}`
        };
        const connectDetails = {
            nonce: this.challenge?.nonce,
            token: params.auth.token,
            clientId: params.client.id,
            clientMode: params.client.mode,
            role: params.role,
            scopes: params.scopes.join(",")
        };
        params["device"] = await this.securityAdapter.openClawSignFlow(connectDetails);
        const frame = JSON.stringify({
            type: "req",
            id: this.connectRequestId,
            method: "connect",
            params
        });
        this.ws.send(frame);
        // Set up a pending handler for the connect response
        const timer = setTimeout(() => {
            this.pendingRpc.delete(this.connectRequestId);
            console.error("[OpenClaw] Connect handshake timed out");
            this.setStatus("error");
        }, 15_000);
        this.pendingRpc.set(this.connectRequestId, {
            resolve: (payload) => {
                if (payload?.type === "hello-ok") {
                    this.setStatus("connected");
                }
            },
            reject: (err) => {
                console.error("[OpenClaw] Connect rejected:", err);
                this.setStatus("error");
            },
            timer
        });
    }
    // -------------------------------------------------------------------------
    // Reconnect logic (exponential backoff: 800ms → 15s)
    // -------------------------------------------------------------------------
    scheduleReconnect() {
        this.clearReconnectTimer();
        const delay = Math.min(800 * Math.pow(2, this.reconnectAttempt), 15_000);
        this.reconnectAttempt++;
        console.log(`[OpenClaw] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempt})`);
        this.reconnectTimer = setTimeout(() => this.connect(), delay);
    }
    clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
    // -------------------------------------------------------------------------
    // Helpers
    // -------------------------------------------------------------------------
    setStatus(status) {
        if (this.status === status)
            return;
        this.status = status;
        this.config.onStatusChange?.(status);
    }
    rejectAllPending(reason) {
        for (const [id, pending] of this.pendingRpc) {
            clearTimeout(pending.timer);
            pending.reject(new Error(reason));
        }
        this.pendingRpc.clear();
    }
    // -------------------------------------------------------------------------
    // Agent file operations (agents.files.list / get / set)
    // -------------------------------------------------------------------------
    async getSkillsStatus(agentId) {
        const resp = await this.rpc("skills.status", agentId ? { agentId } : {});
        return resp?.skills ?? [];
    }
    async getPlugins() {
        const resp = await this.rpc("config.get");
        return resp?.config?.plugins ?? {};
    }
    async listAgentFiles(agentId) {
        const resp = await this.rpc("agents.files.list", { agentId });
        return resp?.files ?? [];
    }
    async getAgentFile(agentId, name) {
        const resp = await this.rpc("agents.files.get", { agentId, name });
        return resp?.file ?? null;
    }
    async setAgentFile(agentId, name, content) {
        try {
            await this.rpc("agents.files.set", { agentId, name, content });
            return { ok: true, message: "File saved" };
        }
        catch (err) {
            return { ok: false, message: `agents.files.set failed: ${err}` };
        }
    }
    async getSessionsUsage() {
        try {
            const resp = await this.rpc("sessions.usage", {});
            return resp ?? null;
        }
        catch (err) {
            console.error("[OpenClaw] sessions.usage failed:", err);
            return null;
        }
    }
    async patchToolsDeny(deny) {
        try {
            // Fetch current config to get the base hash (prevents concurrent overwrites)
            const getResp = await this.rpc("config.get", {});
            const configHash = getResp?.hash;
            if (!configHash) {
                return { success: false, message: `config.get returned no hash: ${JSON.stringify(getResp)}` };
            }
            await this.rpc("config.patch", {
                baseHash: configHash,
                raw: JSON.stringify({ tools: { deny } })
            });
            // Fire-and-forget — gateway may restart before the response arrives
            this.rpc("gateway.restart.request", {}).catch(() => { });
            return { success: true, message: `tools.deny=${JSON.stringify(deny)} applied; gateway restarting` };
        }
        catch (err) {
            return { success: false, message: `Gateway error: ${err}` };
        }
    }
}
